import { Box, useDisclosure } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'ui-components';
import { PlatformTikTokColorIcon } from 'ui-components/dist/PlatformTikTokColorIcon';
import {
  CONTENT_TYPES,
  SNACKBAR_ERROR_GENERIC_OPTIONS,
  SOCIAL_PLATFORMS,
} from '~/constants';
import { captureException } from '~/errorsLogging';
import { selectCreator } from '~/selectors/creator';
import {
  CreatorBenchmarks,
  DefaultMediaPackType,
  InstagramInsightType,
  TiktokInsightType,
  TiktokType,
  ValueOf,
} from '~/types';

import { mediaPackContentToIds } from '../InstagramSection/InstagramSection.mappers';
import { MediaPackDetailSelectContentModal } from '../MediaPackDetailSelectContentModal';
import { UpdateContentSubmitValues } from '../MediaPackDetailSelectContentModal/MediaPackDetailSelectContentModal.types';
import { HighlightedContentSection } from '../MediaPacksDefaultTemplate/HighlightedContentSection';
import { OverviewSection } from '../MediaPacksDefaultTemplate/OverviewSection';
import { mapSubmitDataToUpdateMediaPackValues } from '../MediaPacksDefaultTemplate/OverviewSection/OverviewSection.mapper';
import {
  DEFAULT_TIKTOK_METRIC_1,
  DEFAULT_TIKTOK_METRIC_2,
  DEFAULT_TIKTOK_METRIC_3,
} from './TiktokSection.constants';
import {
  mapTiktokContentToHighlightedContentProps,
  mapTiktokMetricsToMetricCardData,
} from './TiktokSection.mappers';

interface Props {
  mediaPack: DefaultMediaPackType;
  creatorBenchmarks: CreatorBenchmarks;
  creatorInsights: InstagramInsightType;
  ttInsights: TiktokInsightType;
  ttContents: TiktokType[];
  onUpdateMediaPack: (submitValues: UpdateContentSubmitValues) => void;
  isSharedView?: boolean;
}

export const TiktokSection: React.FC<Props> = ({
  mediaPack,
  creatorBenchmarks,
  ttInsights,
  ttContents,
  onUpdateMediaPack,
  isSharedView,
}) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const creator = useSelector(selectCreator);
  const snackbar = useSnackbar();

  const {
    metric1 = DEFAULT_TIKTOK_METRIC_1,
    metric2 = DEFAULT_TIKTOK_METRIC_2,
    metric3 = DEFAULT_TIKTOK_METRIC_3,
  } = mediaPack?.layout?.metricsPosition?.tiktok || {};

  const metrics = metric1.concat(metric2, metric3);
  const reducedMetrics = mapTiktokMetricsToMetricCardData(
    metrics,
    creatorBenchmarks,
  );

  const handleSubmit = async (
    selectedContent: TiktokType[],
    selectedContentType: ValueOf<typeof CONTENT_TYPES>,
    platform: SOCIAL_PLATFORMS,
  ) => {
    const mediaPackValues = mapSubmitDataToUpdateMediaPackValues<TiktokType>({
      selectedContent,
      selectedContentType,
      platform,
    });

    try {
      await onUpdateMediaPack(mediaPackValues);
      onClose();
    } catch (error) {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
      captureException(error);
    }
  };

  if (reducedMetrics.length === 0) {
    return null;
  }

  return (
    <Box>
      {!isSharedView && (
        <MediaPackDetailSelectContentModal
          initialSelectedItemIds={mediaPackContentToIds(ttContents)}
          contentType={CONTENT_TYPES.tiktok}
          isOpen={isOpen}
          onCancel={onClose}
          onClose={onClose}
          onSubmit={handleSubmit}
          shouldShowSelectionPreview
          creatorId={mediaPack.creator.id}
        />
      )}

      <OverviewSection
        data-testid={`${SOCIAL_PLATFORMS.tiktok}-overview-section`}
        overviewTitle="TikTok Overview"
        metrics={reducedMetrics}
        ages={ttInsights?.ages}
        countries={ttInsights?.countries?.slice(0, 3)}
        genders={ttInsights?.genders}
        icon={PlatformTikTokColorIcon}
        creatorBenchmarks={creatorBenchmarks}
        mediaPack={mediaPack}
        platform={SOCIAL_PLATFORMS.tiktok}
        isSharedView={isSharedView}
        insightsLastUpdatedAt={ttInsights.lastUpdatedAt}
      />

      <HighlightedContentSection
        marginTop={14}
        sectionTitle="TikTok Highlights"
        contents={mapTiktokContentToHighlightedContentProps({
          hasManager: Boolean(creator?.manager?.id),
          ttContents,
          isSharedView,
        })}
        onEdit={onOpen}
        isSharedView={isSharedView}
      />
    </Box>
  );
};
